import React, { useState } from 'react'

import { Icon, Icons, Popover } from '@clientbase/clientbase-library'
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded'
import {
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useAuthContext } from 'context/AuthContext'
import { useSectionPermission } from 'hooks/usePermission'
import Image from 'next/image'
import { useRouter } from 'next/router'

import {
  AccountSwitcherList,
  AccountSwitcherStyles,
  groupSubtitle,
  useShowAccountSwitcher
} from 'components/AccountSwitcher'
import CanLogical from 'components/CanLogical'

import { theme } from 'styles/theme'

import * as S from './styles'

export type Setting = {
  action: () => void
  icon: Icons
  label: string
}

type MerchantAccountProps = {
  setIsOpenModalReport: React.Dispatch<React.SetStateAction<boolean>>
}

const MerchantAccount = ({ setIsOpenModalReport }: MerchantAccountProps) => {
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null)
  // Tooltip controlado: o menu abre no mousedown com um fundo por cima da tela,
  // então o mouseleave do ícone não dispara e o tooltip ficava preso aberto
  // (inclusive por cima do menu). Com o menu aberto ele não aparece.
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const match = useMediaQuery('(max-width: 770px)', { noSsr: false })

  const routerGlobal = useRouter()
  const { signOut, user, groupSession, refreshAccounts } = useAuthContext()
  // Relatórios deixou de ter entrada no menu lateral: este item virou o único
  // acesso. Por isso o gate passou a ser a permissão (`reports:show`) em vez da
  // lista de roles que existia aqui — ela escondia o item de `operator` e
  // `financial_operator`, que têm a permissão e alcançavam Relatórios pelo menu.
  // Barrar por role agora tiraria o acesso deles de vez.
  const canSeeReports = useSectionPermission('reports:show')

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (match) return

    setIsTooltipOpen(false)
    setAnchorElUser(event.currentTarget)
    // SC-10999: a lista gravada no login pode estar velha (convite aceito em
    // outra aba, acesso removido). Atualiza sem travar a abertura do menu.
    refreshAccounts().catch(() => null)
  }

  const handleCloseUserMenu = () => {
    setAnchorElUser(null)
  }

  // SC-10999: seletor de contas só para quem tem mais de uma empresa ou algum
  // grupo. Para os demais o menu fica exatamente como antes.
  const showAccountSwitcher = useShowAccountSwitcher()

  const settings: Setting[] = [
    {
      action: () => {
        routerGlobal.push('/settings')
        handleCloseUserMenu()
      },
      icon: 'settings',
      label: 'Configurações'
    }
  ]

  if (canSeeReports) {
    settings.push({
      action: () => {
        setIsOpenModalReport(true)
        handleCloseUserMenu()
      },
      icon: 'assignment',
      label: 'Relatórios'
    })
  }

  if (
    CanLogical({
      roles: ['guest']
    })
  ) {
    const ConfigsSettingsIndex = settings.findIndex(
      ({ label }) => label === 'Configurações'
    )
    settings.splice(ConfigsSettingsIndex, 1)
  }

  return (
    // O mousedown fica no gatilho e não na div: o menu é renderizado em portal,
    // mas os eventos dele sobem pela árvore do React até aqui. Com o handler e o
    // tooltip na div, mexer o mouse dentro do menu reabria o tooltip.
    <div>
      {!match ? (
        <Tooltip
          title="Configurações"
          open={isTooltipOpen && !anchorElUser}
          onOpen={() => setIsTooltipOpen(true)}
          onClose={() => setIsTooltipOpen(false)}
        >
          <IconButton
            aria-label="Configurações"
            onMouseDown={handleOpenUserMenu}
          >
            <Icon icon="settings" colorSX={theme.palette.neutral[400]} />
          </IconButton>
        </Tooltip>
      ) : (
        <S.MobileBox onMouseDown={handleOpenUserMenu}>
          <S.MobileLogo>
            <Image
              src={user?.logo || '/favicon-32x32.png'}
              width={32}
              height={32}
              objectFit="contain"
              style={{ aspectRatio: '1/1' }}
              alt="Logo da empresa"
            />
          </S.MobileLogo>
          <S.MobileName>{user?.merchant_user_name ?? user?.name}</S.MobileName>
        </S.MobileBox>
      )}

      <Popover
        anchorEl={anchorElUser}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'top'
        }}
        keepMounted={false}
        onClose={handleCloseUserMenu}
        open={Boolean(anchorElUser)}
        sx={{ maxWidth: '950px', minWidth: '300px', mt: 5 }}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top'
        }}
      >
        {/* SC-10999: cabeçalho com o contexto atual (empresa ou visão do grupo)
            e quem está logado. Não é clicável: trocar de contexto é nas listas
            abaixo. */}
        <S.CurrentContext>
          {groupSession ? (
            <AccountSwitcherStyles.GroupIconWrapper size={40}>
              <GridViewRoundedIcon sx={{ fontSize: 22 }} />
            </AccountSwitcherStyles.GroupIconWrapper>
          ) : (
            <S.LogoWrapper>
              <Image
                src={user?.logo || '/only-icon.png'}
                width={40}
                height={40}
                objectFit="contain"
                style={{ aspectRatio: '1/1' }}
                alt="Logo da empresa no menu"
              />
            </S.LogoWrapper>
          )}
          <Stack minWidth={0}>
            <Typography variant="smBold" noWrap>
              {groupSession ? 'Todas as empresas' : user?.name}
            </Typography>
            <Typography
              variant="xsLight"
              color={theme.palette.neutral[400]}
              noWrap
            >
              {groupSession
                ? groupSubtitle(
                    groupSession.name,
                    groupSession.merchants.length
                  )
                : user?.merchant_user_name}
            </Typography>
          </Stack>
        </S.CurrentContext>

        <Divider />

        {showAccountSwitcher && (
          <AccountSwitcherList onClose={handleCloseUserMenu} />
        )}

        {/* Respiro abaixo do divisor: sem ele "Configurações" colava na linha
            que fecha a lista de contas */}
        {showAccountSwitcher && <Divider sx={{ mt: 0.5, mb: 1 }} />}

        {settings.map((setting) => {
          return (
            <MenuItem
              key={setting.label}
              onClick={setting.action}
              sx={{ borderRadius: '6px' }}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                minWidth={'280px'}
              >
                {!!setting.icon && <Icon icon={setting.icon} width="23px" />}
                <Typography>{setting.label}</Typography>
              </Stack>
            </MenuItem>
          )
        })}

        <S.MenuItem
          onClick={async () => {
            await signOut()
            handleCloseUserMenu()
          }}
        >
          <Icon icon={'exitToApp'} width="23px" />
          <Typography>Sair</Typography>
        </S.MenuItem>
      </Popover>
    </div>
  )
}
export default MerchantAccount
